import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Space, Tag, Tabs, Form, Input, App, Tooltip, Alert } from 'antd';
import { EditOutlined, ArrowLeftOutlined, StopOutlined, CheckCircleOutlined, MailOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import { useMemo, useState } from 'react';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { withDevModalFilePath, NumericInput, PhoneInput } from '../../../components/common';

type AgencyCommissionRecord = {
  id: string;
  periodStart: string;
  periodEnd?: string | null;
  totalSales: string;
  commissionAmount: string;
  paymentStatus: 'pending' | 'prepared' | 'paid' | 'canceled' | string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAgencyCommissionRecord = (value: unknown): AgencyCommissionRecord | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.periodStart !== 'string') {
    return null;
  }

  return {
    id: value.id,
    periodStart: value.periodStart,
    periodEnd: typeof value.periodEnd === 'string' ? value.periodEnd : null,
    totalSales: typeof value.totalSales === 'string' ? value.totalSales : '0',
    commissionAmount: typeof value.commissionAmount === 'string' ? value.commissionAmount : '0',
    paymentStatus: typeof value.paymentStatus === 'string' ? value.paymentStatus : 'pending',
  };
};

const { Modal } = withDevModalFilePath('client/src/pages/admin/agencies/AgencyDetailPage.tsx');

export function AgencyDetailPage() {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [form] = Form.useForm();
  const utils = trpc.useUtils();

  // 代理店詳細取得
  const { data: agency } = trpc.agencyAdmin.getAgencyUserById.useQuery(
    { agencyUserId: id! },
    { enabled: !!id }
  );

  const { data: commissionsData } = trpc.agencyAdmin.listCommissions.useQuery({
    agencyUserId: id!,
    page: 1,
    limit: 10,
  }, { enabled: !!id });

  // 代理店更新
  const updateMutation = trpc.agencyAdmin.updateAgencyUser.useMutation({
    onSuccess: () => {
      message.success('代理店情報を更新しました');
      setIsEditModalOpen(false);
      utils.agencyAdmin.getAgencyUserById.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '更新に失敗しました');
    },
  });

  // 停止/再開
  const suspendMutation = trpc.agencyAdmin.suspendAgencyUser.useMutation({
    onSuccess: (_, variables) => {
      const input = isRecord(variables) ? variables : null;
      const suspend = typeof input?.suspend === 'boolean' ? input.suspend : false;
      message.success(suspend ? '代理店を停止しました' : '代理店を再開しました');
      utils.agencyAdmin.getAgencyUserById.invalidate();
      utils.agencyAdmin.listAgencyUsers.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '操作に失敗しました');
    },
  });

  // 招待メール再送
  const resendInvitationMutation = trpc.agencyInvitation.resend.useMutation({
    onSuccess: () => {
      message.success('招待メールを再送しました');
    },
    onError: (error) => {
      message.error(error.message || '招待メールの再送に失敗しました');
    },
  });

  const commissionRows = useMemo(
    () => (commissionsData?.commissions ?? []).flatMap((commission) => {
      const normalized = normalizeAgencyCommissionRecord(commission);
      return normalized ? [normalized] : [];
    }),
    [commissionsData?.commissions]
  );

  const handleEdit = () => {
    form.setFieldsValue(agency);
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    form.validateFields().then((values) => {
      updateMutation.mutate({
        agencyUserId: id!,
        ...values,
      });
    });
  };

  const handleSuspend = () => {
    Modal.confirm({
      title: agency?.isActive ? '代理店を停止しますか？' : '代理店を再開しますか？',
      content: agency?.isActive
        ? '停止すると、この代理店はログインできなくなります。'
        : 'この代理店のログインを再開します。',
      okText: agency?.isActive ? '停止' : '再開',
      okType: agency?.isActive ? 'danger' : 'primary',
      cancelText: 'キャンセル',
      onOk: () => {
        suspendMutation.mutate({
          agencyUserId: id!,
          suspend: !agency?.isActive,
          suspendedReason: agency?.isActive ? '管理者による停止' : undefined,
        });
      },
    });
  };

  const commissionColumns: ResponsiveColumnType<AgencyCommissionRecord>[] = [
    {
      title: '期間',
      key: 'period',
      render: (_, record) => {
        const start = new Date(record.periodStart).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit' });
        return start;
      },
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '総売上',
      dataIndex: 'totalSales',
      key: 'totalSales',
      render: (sales) => `¥${parseFloat(sales).toLocaleString()}`,
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 2,
    },
    {
      title: '手数料額',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => `¥${parseFloat(amount).toLocaleString()}`,
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: 'ステータス',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status) => {
        const map: Record<string, { color: string; label: string }> = {
          pending: { color: 'orange', label: '未払い' },
          prepared: { color: 'blue', label: '準備完了' },
          paid: { color: 'green', label: '支払済み' },
          canceled: { color: 'red', label: 'キャンセル' },
        };
        const info = map[status] || { color: 'default', label: status };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 4,
    },
  ];

  const tabItems = [
    {
      key: 'info',
      label: '基本情報',
      children: (
        <Card>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="代理店ID">{agency?.id}</Descriptions.Item>
            <Descriptions.Item label="クーポンプレフィックス">
              <code style={{ fontWeight: 'bold', fontSize: '16px' }}>{agency?.couponPrefix}</code>
            </Descriptions.Item>
            <Descriptions.Item label="代理店名">{agency?.name}</Descriptions.Item>
            <Descriptions.Item label="会社名">{agency?.companyName || '-'}</Descriptions.Item>
            <Descriptions.Item label="メールアドレス">{agency?.email}</Descriptions.Item>
            <Descriptions.Item label="電話番号">{agency?.phone}</Descriptions.Item>
            <Descriptions.Item label="代理店階層">{agency?.agencyTier}</Descriptions.Item>
            <Descriptions.Item label="手数料レート">{parseFloat(agency?.commissionRate || '0')}%</Descriptions.Item>
            <Descriptions.Item label="ステータス">
              <Space direction="vertical" size="small">
                {agency?.isActive ? <Tag color="green">有効</Tag> : <Tag color="red">停止</Tag>}
                {agency?.emailVerified ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">メール認証済み</Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="warning">メール未認証</Tag>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="登録日">
              {agency?.createdAt ? new Date(agency.createdAt).toLocaleString('ja-JP') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最終ログイン">
              {agency?.lastLoginAt ? new Date(agency.lastLoginAt).toLocaleString('ja-JP') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ログイン回数">{agency?.loginCount || 0}回</Descriptions.Item>
            <Descriptions.Item label="住所" span={2}>
              {agency?.postalCode && `〒${agency.postalCode}`}<br />
              {agency?.fullAddress}<br />
              {agency?.building}
            </Descriptions.Item>
            <Descriptions.Item label="運営側メモ" span={2}>
              {agency?.note || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'bank',
      label: '支払先情報',
      children: (
        <Card>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="銀行名">{agency?.bankName}</Descriptions.Item>
            <Descriptions.Item label="支店名">{agency?.bankBranchName}</Descriptions.Item>
            <Descriptions.Item label="口座種別">{agency?.bankAccountType}</Descriptions.Item>
            <Descriptions.Item label="口座番号">{agency?.bankAccountNumber}</Descriptions.Item>
            <Descriptions.Item label="口座名義">{agency?.bankAccountHolder}</Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'commissions',
      label: '手数料履歴',
      children: (
        <Card>
          <ResponsiveTable<AgencyCommissionRecord>
            dataSource={commissionRows}
            columns={commissionColumns}
            rowKey="id"
            pagination={false}
            mobileCardConfig={{
              titleField: 'periodStart',
            }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(adminPath('agencies'))}>
              戻る
            </Button>
            <h1 style={{ margin: 0 }}>代理店詳細</h1>
          </Space>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              編集
            </Button>
            <Button
              icon={agency?.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={handleSuspend}
              danger={agency?.isActive}
            >
              {agency?.isActive ? '停止' : '再開'}
            </Button>
          </Space>
        </div>

        {!agency?.emailVerified && (
          <Alert
            message="メール認証未完了"
            description={
              <Space direction="vertical">
                <div>この代理店はまだメール認証を完了していません。初回パスワードが設定されていません。</div>
                <Tooltip title="初回パスワード設定用の招待メールを再送します">
                  <Button
                    type="primary"
                    size="small"
                    icon={<MailOutlined />}
                    loading={resendInvitationMutation.isPending}
                    onClick={() =>
                      resendInvitationMutation.mutate({
                        agencyUserId: id!,
                      })
                    }
                  >
                    招待メール再送
                  </Button>
                </Tooltip>
              </Space>
            }
            type="warning"
            icon={<CloseCircleOutlined />}
            showIcon
          />
        )}

        <Tabs items={tabItems} />
      </Space>

      <Modal
        title="代理店情報編集"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={handleUpdate}
        confirmLoading={updateMutation.isPending}
        okText="更新"
        cancelText="キャンセル"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="代理店名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="companyName" label="会社名">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="電話番号" rules={[{ required: true }]}>
            <PhoneInput />
          </Form.Item>
          <Form.Item name="agencyTier" label="代理店階層" rules={[{ required: true }]}>
            <NumericInput min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="commissionRate" label="手数料レート（%）" rules={[{ required: true }]}>
            <NumericInput min={0} max={100} step={0.5} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="運営側メモ">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
